import React from 'react';
import { useDarkMode } from '../../context/AppContext';
import GameCard from '../../components/GameCard/GameCard';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Data from '../data.json';
import BlogCard from '../../components/BlogCard/BlogCard';
import { slugify } from '../../utils/constants';

/* ── Section Header ── */
const SectionHeader = ({ emoji, title, viewAllLink, gradient }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
    <h2 className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 20 }}>{emoji}</span>
      <span style={gradient ? { background: gradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' } : {}}>
        {title}
      </span>
    </h2>
    {viewAllLink && (
      <Link
        to={viewAllLink}
        style={{
          fontSize: 12, fontWeight: 700, color: 'var(--accent)',
          textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4,
          padding: '5px 12px', borderRadius: 8, border: '1px solid var(--accent)',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(79,142,247,0.12)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        View All
        <svg width="12" height="12" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
        </svg>
      </Link>
    )}
  </div>
);

/* ── Games Grid ── */
const GamesGrid = ({ games, cols = 'grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-10' }) => (
  <div className={`grid ${cols} gap-2 sm:gap-3`}>
    {games.map((_ele, i) => {
      if (!_ele?.name?.en) return null;
      const slug = slugify(_ele.name.en);
      return <GameCard key={i} slug={slug} name={_ele.name.en} banner={_ele?.assets?.square} />;
    })}
  </div>
);

const Home = () => {
  const { gamesList } = useDarkMode();

  return (
    <div style={{ padding: '20px 16px 32px' }}>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>Play Free Online Games | TheGameZone — Action, Puzzle, Racing & More</title>
        <meta name="description" content="Play 500+ free online games instantly at TheGameZone. No download, no sign-up. Action, puzzle, racing, sports, arcade & multiplayer games on any device." />
        <meta name="keywords" content="free online games, play games online, browser games, action games, puzzle games, racing games, arcade games, multiplayer games, TheGameZone, no download games, HTML5 games" />
        <meta property="og:title" content="Play Free Online Games | TheGameZone" />
        <meta property="og:description" content="Play 500+ free online games instantly — no download, no sign-up. Action, puzzle, racing, sports & more at TheGameZone." />
        <meta property="og:url" content="https://thegamezone.fun/" />
        <meta property="og:image" content="https://thegamezone.fun/logo.png" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="TheGameZone" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Play Free Online Games | TheGameZone" />
        <meta name="twitter:description" content="Play 500+ free online games instantly — no download, no sign-up." />
        <meta name="twitter:image" content="https://thegamezone.fun/logo.png" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://thegamezone.fun/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "TheGameZone",
          "url": "https://thegamezone.fun",
          "description": "Play 500+ free online games instantly. No download, no sign-up required.",
          "potentialAction": { "@type": "SearchAction", "target": "https://thegamezone.fun/search?q={search_term}", "query-input": "required name=search_term" }
        })}</script>
      </Helmet>

      {gamesList?.length > 0 && (
        <section style={{ marginBottom: 28 }}>

          {/* Desktop grid - hidden on mobile */}
          <div className="hidden md:grid" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gridTemplateRows: '140px 140px', gap: 6, borderRadius: 14, overflow: 'hidden' }}>
            {gamesList[0] && (() => {
              const slug0 = slugify(gamesList[0].name.en);
              return (
                <Link to={`/game/${slug0}`} className="game-card-hover"
                  style={{ gridRow: '1 / 3', gridColumn: '1 / 2', position: 'relative', display: 'block', textDecoration: 'none' }}>
                  <img src={gamesList[0]?.assets?.cover || gamesList[0]?.assets?.square} alt={gamesList[0].name.en} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 55%)' }} />
                  <div style={{ position: 'absolute', bottom: 14, left: 14 }}>
                    <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 22, color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.8)', marginBottom: 6 }}>{gamesList[0].name.en}</div>
                    <span style={{ background: '#7c3aed', color: '#fff', padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 800 }}>▶ Play Now</span>
                  </div>
                </Link>
              );
            })()}
            {gamesList.slice(1, 9).map((_ele, i) => {
              const slug = slugify(_ele.name.en);
              const badges = ['hot', 'hot', null, 'hot', null, 'updated', 'hot', null];
              return (
                <Link key={i} to={`/game/${slug}`} className="game-card-hover"
                  style={{ position: 'relative', display: 'block', textDecoration: 'none' }}>
                  <img src={_ele?.assets?.cover || _ele?.assets?.square} alt={_ele.name.en} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 60%)' }} />
                  {badges[i] && (
                    <span style={{ position: 'absolute', top: 6, left: 6, background: badges[i] === 'hot' ? '#ef4444' : '#4f8ef7', color: '#fff', fontSize: 9, fontWeight: 900, padding: '2px 7px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {badges[i] === 'hot' ? '🔥 Hot' : '↻ Updated'}
                    </span>
                  )}
                  <div style={{ position: 'absolute', bottom: 6, left: 8, right: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>{_ele.name.en}</div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Mobile hero - hidden on md+ */}
          <div className="flex md:hidden flex-col" style={{ gap: 4 }}>
            {gamesList[0] && (() => {
              const slug0 = slugify(gamesList[0].name.en);
              return (
                <Link to={`/game/${slug0}`} className="game-card-hover"
                  style={{ display: 'block', position: 'relative', aspectRatio: '16/8', borderRadius: 12, overflow: 'hidden', textDecoration: 'none' }}>
                  <img src={gamesList[0]?.assets?.cover || gamesList[0]?.assets?.square} alt={gamesList[0].name.en}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, transparent 55%)' }} />
                  <div style={{ position: 'absolute', bottom: 12, left: 12 }}>
                    <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 14, color: '#fff', marginBottom: 5, textShadow: '0 2px 6px rgba(0,0,0,0.9)' }}>{gamesList[0].name.en}</div>
                    <span style={{ background: '#7c3aed', color: '#fff', padding: '3px 12px', borderRadius: 20, fontSize: 11, fontWeight: 800 }}>▶ Play Now</span>
                  </div>
                </Link>
              );
            })()}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 4 }}>
              {gamesList.slice(1, 5).map((_ele, i) => {
                const slug = slugify(_ele.name.en);
                return (
                  <Link key={i} to={`/game/${slug}`} className="game-card-hover"
                    style={{ display: 'block', position: 'relative', aspectRatio: '1', borderRadius: 10, overflow: 'hidden', textDecoration: 'none' }}>
                    <img src={_ele?.assets?.square || _ele?.assets?.cover} alt={_ele.name.en}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 55%)' }} />
                    <div style={{ position: 'absolute', bottom: 3, left: 4, right: 4 }}>
                      <div style={{ fontSize: 8, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textShadow: '0 1px 3px rgba(0,0,0,1)' }}>{_ele.name.en}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

        </section>
      )}

      {/* Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" style={{ marginBottom: 28 }}>
        {[
          { icon: '🎮', label: '500+ Games', sub: 'Free to play' },
          { icon: '⚡', label: 'Instant Play', sub: 'No download' },
          { icon: '📱', label: 'All Devices', sub: 'Mobile friendly' },
          { icon: '🔓', label: 'No Sign-up', sub: '100% free' },
        ].map((s, i) => (
          <div key={i} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 22 }}>{s.icon}</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)' }}>{s.label}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.sub}</div>
            </div>
          </div>
        ))}
      </div>

      {/* All Games */}
      <section style={{ marginBottom: 28 }}>
        <SectionHeader emoji="🎮" title="All Games" viewAllLink="/category/all-games" gradient="linear-gradient(135deg, #4f8ef7, #a855f7)" />
        <div className="hidden md:block"><GamesGrid games={Data.games?.slice(4, 32) || []} /></div>
        <div className="block md:hidden"><GamesGrid games={Data.games?.slice(4, 13) || []} /></div>
      </section>

      {/* Trending Games */}
      <section style={{ marginBottom: 28 }}>
        <SectionHeader emoji="🔥" title="Trending Games" viewAllLink="/category/all-games" gradient="linear-gradient(135deg, #ef4444, #f97316)" />
        <div className="hidden md:block"><GamesGrid games={Data.games?.slice(12, 24) || []} /></div>
        <div className="block md:hidden"><GamesGrid games={Data.games?.slice(12, 21) || []} /></div>
      </section>

      {/* Featured + Sidebar */}
      <section style={{ marginBottom: 28 }}>
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
          <div className="xl:col-span-3">
            <SectionHeader emoji="💎" title="Featured Games" gradient="linear-gradient(135deg, #06b6d4, #3b82f6)" />
            <div className="hidden md:block"><GamesGrid games={Data.games?.slice(24, 40) || []} cols="grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6" /></div>
            <div className="block md:hidden"><GamesGrid games={Data.games?.slice(24, 33) || []} cols="grid-cols-3" /></div>
          </div>
          <div className="xl:col-span-1 space-y-4">
            <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 14, border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                ✨ Quick Play
              </div>
              {Data.games?.slice(0, 4)?.map((_ele, i) => {
                if (!_ele?.name?.en) return null;
                const slug = slugify(_ele.name.en);
                return (
                  <Link key={i} to={`/game/${slug}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: i < 3 ? '1px solid var(--border)' : 'none', textDecoration: 'none' }}
                  >
                    <img src={_ele?.assets?.square} alt={_ele.name.en} style={{ width: 32, height: 32, borderRadius: 7, objectFit: 'cover' }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {_ele.name.en}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section style={{ marginBottom: 28 }}>
        <SectionHeader emoji="📝" title="Gaming News & Blogs" viewAllLink="/blog" gradient="linear-gradient(135deg, #22c55e, #059669)" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {gamesList?.slice(30, 42)?.map((_ele, i) => {
            if (!_ele?.name?.en) return null;
            const slug = slugify(_ele.name.en);
            return <BlogCard key={i} id={i} slug={slug} name={_ele.name.en} banner={_ele?.assets?.cover} img={_ele?.assets?.square} _ele={_ele} />;
          })}
        </div>
      </section>

      {/* ── SEO Rich Text Content ── */}
      <section style={{ marginBottom: 28 }}>

        {/* Block 1 — What is TheGameZone */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: '24px 26px', marginBottom: 16 }}>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: 'var(--text-primary)', marginBottom: 12, borderLeft: '3px solid #4f8ef7', paddingLeft: 12 }}>
            🎮 Play Free Online Games — No Download Needed
          </h2>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.9, marginBottom: 10 }}>
            <strong style={{ color: 'var(--text-primary)' }}>TheGameZone</strong> is one of the best free online gaming platforms where you can play hundreds of browser-based games instantly — no downloads, no installations, no sign-ups required. Whether you're looking for fast-paced <Link to="/category/action" style={{ color: '#4f8ef7', textDecoration: 'none', fontWeight: 700 }}>action games</Link>, brain-teasing <Link to="/category/puzzle" style={{ color: '#4f8ef7', textDecoration: 'none', fontWeight: 700 }}>puzzle games</Link>, exciting <Link to="/category/racing" style={{ color: '#4f8ef7', textDecoration: 'none', fontWeight: 700 }}>racing games</Link>, or fun <Link to="/category/casual" style={{ color: '#4f8ef7', textDecoration: 'none', fontWeight: 700 }}>casual games</Link> — we have something for every type of player.
          </p>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.9 }}>
            Our platform is designed for speed and simplicity. Every game loads directly in your browser within seconds and works seamlessly on desktop computers, laptops, tablets, and smartphones. We continuously update our game library so there's always something new and exciting to discover every day.
          </p>
        </div>

        {/* Block 2 — Game Categories */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: '24px 26px', marginBottom: 16 }}>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: 'var(--text-primary)', marginBottom: 12, borderLeft: '3px solid #a855f7', paddingLeft: 12 }}>
            🕹️ Explore All Game Categories
          </h2>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.9, marginBottom: 14 }}>
            TheGameZone offers a massive collection of games across dozens of categories. Here's what you can play right now:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            {[
              { cat: 'action', title: '⚔️ Action Games', desc: 'High-intensity combat, fighting, and adventure games that test your reflexes and speed.' },
              { cat: 'puzzle', title: '🧩 Puzzle Games', desc: 'Mind-bending challenges, logic games, and brain teasers to sharpen your thinking.' },
              { cat: 'racing', title: '🏎️ Racing Games', desc: 'Thrilling car races, bike stunts, and speed challenges on exciting tracks.' },
              { cat: 'sports', title: '⚽ Sports Games', desc: 'Football, basketball, cricket, and more — play your favorite sports online.' },
              { cat: 'arcade', title: '🕹️ Arcade Games', desc: 'Classic retro-style games and modern arcade hits for endless fun.' },
              { cat: 'multiplayer', title: '👥 Multiplayer Games', desc: 'Challenge friends or players worldwide in real-time online multiplayer games.' },
              { cat: 'casual', title: '🎲 Casual Games', desc: 'Easy-to-play, relaxing games perfect for quick sessions anytime.' },
              { cat: 'hypercasual', title: '⚡ Hypercasual Games', desc: 'One-tap games with simple mechanics and addictive gameplay loops.' },
            ].map(({ cat, title, desc }) => (
              <div key={cat} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
                <div style={{ flex: 1 }}>
                  <Link to={`/category/${cat}`} style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', textDecoration: 'none', display: 'block', marginBottom: 3 }}
                    onMouseEnter={e => e.currentTarget.style.color = '#4f8ef7'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-primary)'}
                  >{title}</Link>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.65, margin: 0 }}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Block 3 — Why Play + Benefits */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: '24px 26px', marginBottom: 16 }}>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: 'var(--text-primary)', marginBottom: 12, borderLeft: '3px solid #22c55e', paddingLeft: 12 }}>
            ✅ Why Play Games on TheGameZone?
          </h2>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.9, marginBottom: 14 }}>
            Playing online games isn't just entertainment — it has real cognitive and emotional benefits. Here's why millions of players choose TheGameZone every day:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { icon: '🧠', title: 'Improves Cognitive Skills', desc: 'Strategy and puzzle games enhance memory, concentration, and problem-solving abilities.' },
              { icon: '😌', title: 'Reduces Stress', desc: 'Gaming is a proven way to relax, unwind, and take a mental break from daily pressures.' },
              { icon: '⚡', title: 'Boosts Reflexes', desc: 'Action and arcade games sharpen your hand-eye coordination and reaction time.' },
              { icon: '🤝', title: 'Social Connection', desc: 'Multiplayer games help you connect with friends and meet new people worldwide.' },
              { icon: '🎨', title: 'Sparks Creativity', desc: 'Simulation and building games encourage creative thinking and imagination.' },
              { icon: '🏆', title: 'Healthy Competition', desc: 'Leaderboards and challenges motivate you to improve and achieve new personal bests.' },
            ].map((b, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 14px', background: 'var(--bg-hover)', borderRadius: 10, border: '1px solid var(--border)' }}>
                <span style={{ fontSize: 20, flexShrink: 0 }}>{b.icon}</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 3 }}>{b.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>{b.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Block 4 — FAQ */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: '24px 26px', marginBottom: 16 }}>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: 'var(--text-primary)', marginBottom: 16, borderLeft: '3px solid #f59e0b', paddingLeft: 12 }}>
            ❓ Frequently Asked Questions
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {[
              { q: 'Are all games on TheGameZone free to play?', a: 'Yes! Every single game on TheGameZone is 100% free. There are no subscriptions, no hidden fees, and no in-app purchases required to enjoy any game on our platform.' },
              { q: 'Do I need to download anything to play?', a: 'No downloads needed. All games run directly in your web browser using HTML5 technology. Just click Play and start gaming instantly on any device.' },
              { q: 'Can I play on my mobile phone or tablet?', a: 'Absolutely! TheGameZone is fully optimized for all screen sizes. Our games work perfectly on Android and iOS devices, tablets, and desktop computers.' },
              { q: 'Do I need to create an account?', a: 'No registration required. You can play any game on TheGameZone without creating an account or providing any personal information.' },
              { q: 'How often are new games added?', a: 'We add new games regularly to keep our library fresh and exciting. Check back often to discover the latest titles across all categories.' },
              { q: 'Are the games safe for kids?', a: 'Yes, TheGameZone is a family-friendly platform. Our games are suitable for players of all ages and we maintain a safe, ad-appropriate environment.' },
            ].map((faq, i, arr) => (
              <div key={i} style={{ padding: '14px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 6, display: 'flex', gap: 8 }}>
                  <span style={{ color: '#4f8ef7', flexShrink: 0 }}>Q.</span>{faq.q}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8, paddingLeft: 20 }}>
                  <span style={{ color: '#22c55e', fontWeight: 800, marginRight: 6 }}>A.</span>{faq.a}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Block 5 — How to Play */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: '24px 26px', marginBottom: 16 }}>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: 'var(--text-primary)', marginBottom: 14, borderLeft: '3px solid #06b6d4', paddingLeft: 12 }}>
            🚀 How to Start Playing on TheGameZone
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { step: '1', title: 'Browse or Search', desc: 'Explore our homepage, browse by category, or use the search bar to find your favorite game.' },
              { step: '2', title: 'Click to Play', desc: 'Click on any game thumbnail to open the game page. Hit the Play Now button to start instantly.' },
              { step: '3', title: 'Enjoy & Explore', desc: 'Play as long as you want — completely free. Discover related games and new categories.' },
              { step: '4', title: 'Share with Friends', desc: 'Found a great game? Share the link with friends and challenge them to beat your score!' },
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #4f8ef7, #7c3aed)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 900, flexShrink: 0, fontFamily: "'Sora',sans-serif" }}>{s.step}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 3 }}>{s.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7 }}>{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Block 6 — Devices & Compatibility */}
        <div style={{ background: 'linear-gradient(135deg, rgba(79,142,247,0.06), rgba(168,85,247,0.06))', border: '1px solid rgba(79,142,247,0.2)', borderRadius: 16, padding: '24px 26px' }}>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: 'var(--text-primary)', marginBottom: 10 }}>
            📱 Play on Any Device, Anywhere
          </h2>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.9, marginBottom: 14 }}>
            TheGameZone is built with a mobile-first approach, ensuring a smooth and responsive gaming experience across all devices and screen sizes. Whether you're on a Windows PC, Mac, Android phone, iPhone, or iPad — our games adapt perfectly to your screen.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
            {[
              { icon: '🖥️', label: 'Windows PC' },
              { icon: '🍎', label: 'Mac / MacBook' },
              { icon: '📱', label: 'Android Phone' },
              { icon: '📱', label: 'iPhone / iOS' },
              { icon: '📟', label: 'Tablet / iPad' },
              { icon: '💻', label: 'Chromebook' },
            ].map((d, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '10px 8px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textAlign: 'center' }}>
                <span style={{ fontSize: 16 }}>{d.icon}</span>{d.label}
              </div>
            ))}
          </div>
        </div>

      </section>

      {/* ── About Us CTA ── */}
      <section style={{ marginTop: 28 }}>
        <div style={{
          borderRadius: 16, padding: '28px 24px', position: 'relative', overflow: 'hidden',
          background: 'linear-gradient(135deg, #1a1040 0%, #0f1117 50%, #0a1628 100%)',
          border: '1px solid var(--border)', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', flexWrap: 'wrap', gap: 20,
        }}>
          <div style={{ position: 'absolute', top: -40, left: -40, width: 180, height: 180, borderRadius: '50%', background: 'rgba(79,142,247,0.1)', filter: 'blur(40px)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -40, right: -40, width: 180, height: 180, borderRadius: '50%', background: 'rgba(168,85,247,0.1)', filter: 'blur(40px)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 18, background: 'linear-gradient(135deg, #4f8ef7, #a855f7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: 6 }}>
              🎮 About TheGameZone
            </div>
            <p style={{ fontSize: 13, color: '#9aa0b8', lineHeight: 1.7, maxWidth: 480, margin: 0 }}>
              TheGameZone is your ultimate free online gaming hub — no downloads, no sign-ups. Play hundreds of games instantly on any device, anytime.
            </p>
          </div>
          <Link to="/about" style={{ position: 'relative', zIndex: 1, background: 'linear-gradient(135deg, #4f8ef7, #7c3aed)', color: '#fff', padding: '11px 26px', borderRadius: 50, fontWeight: 800, fontSize: 13, textDecoration: 'none', fontFamily: "'Sora',sans-serif", flexShrink: 0 }}>
            Learn More →
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
